/**
 * `/admin/auth/me` permissions 기반 메뉴 권한 헬퍼.
 * authStore에 persist된 MenuPermission[]을 조회한다.
 * full-access role은 전 메뉴 WRITE로 취급한다.
 */

import type {
  EffectivePermission,
  MenuPermission,
} from '@/entities/auth/api/auth.api';
import { isFullAccessRole } from '@/shared/utils/authRoles';

const PERMISSION_RANK: Record<EffectivePermission, number> = {
  NONE: 0,
  READ: 1,
  WRITE: 2,
};

export const getMenuPermission = (
  permissions: MenuPermission[] | undefined | null,
  menuCode: string,
): EffectivePermission => {
  if (!permissions?.length || !menuCode) return 'NONE';
  const found = permissions.find((p) => p.menuCode === menuCode);
  return found?.effectivePermission ?? 'NONE';
};

/** menuCode에 대해 최소 요구 권한(기본 READ) 이상인지 판별 */
export const hasMenuPermission = (
  permissions: MenuPermission[] | undefined | null,
  menuCode: string,
  required: EffectivePermission = 'READ',
): boolean => {
  if (required === 'NONE') return true;
  const current = getMenuPermission(permissions, menuCode);
  return PERMISSION_RANK[current] >= PERMISSION_RANK[required];
};

/** WRITE 권한 여부 — 생성/수정/삭제 UI 노출에 사용 */
export const canWriteMenu = (
  permissions: MenuPermission[] | undefined | null,
  menuCode: string,
): boolean => hasMenuPermission(permissions, menuCode, 'WRITE');

/**
 * 문서 권장 helper — full-access면 항상 true, 아니면 permissions READ 이상.
 */
export const canRead = (
  permissions: MenuPermission[] | undefined | null,
  menuCode: string,
  userRole?: string | null,
): boolean => {
  if (isFullAccessRole(userRole)) return true;
  return hasMenuPermission(permissions, menuCode, 'READ');
};

/**
 * 문서 권장 helper — full-access면 항상 true, 아니면 permissions WRITE.
 */
export const canWrite = (
  permissions: MenuPermission[] | undefined | null,
  menuCode: string,
  userRole?: string | null,
): boolean => {
  if (isFullAccessRole(userRole)) return true;
  return hasMenuPermission(permissions, menuCode, 'WRITE');
};

/**
 * Figma「내 메뉴 권한」·사이드바 top 그룹 순서.
 * API 배열 순서와 무관하게 동일하게 보여준다.
 */
export const TOP_LEVEL_MENU_ORDER = [
  'BASIC_INFO',
  'USER',
  'PARTNER',
  'PRESCRIPTION',
  'SETTLEMENT',
  'MAX_MATCH',
  'NOTICE_FAQ',
] as const;

export type TopLevelMenuCode = (typeof TOP_LEVEL_MENU_ORDER)[number];

/** 사이드바·권한 요약에 쓰는 상위 메뉴 표시명 */
export const TOP_LEVEL_MENU_LABELS: Record<TopLevelMenuCode, string> = {
  BASIC_INFO: '기본 정보',
  USER: '사용자',
  PARTNER: '거래처',
  PRESCRIPTION: '처방',
  SETTLEMENT: '정산',
  MAX_MATCH: '맥스매치',
  NOTICE_FAQ: '공지/FAQ',
};

export const permissionLevelLabel = (
  level: EffectivePermission,
): string => {
  switch (level) {
    case 'WRITE':
      return '편집';
    case 'READ':
      return '조회';
    default:
      return '없음';
  }
};

const maxPermission = (
  a: EffectivePermission,
  b: EffectivePermission,
): EffectivePermission =>
  PERMISSION_RANK[a] >= PERMISSION_RANK[b] ? a : b;

/**
 * top 메뉴 실효 권한.
 * 1) menuCode 직접 항목이 있으면 그대로 사용 (서버 topLevel 정의 존중)
 * 2) 없으면 parentMenuCode가 해당 top인 자식들 중 최고 권한
 */
export const resolveTopLevelPermission = (
  permissions: MenuPermission[] | undefined | null,
  menuCode: TopLevelMenuCode,
): EffectivePermission => {
  if (!permissions?.length) return 'NONE';

  const direct = permissions.find((p) => p.menuCode === menuCode);
  if (direct) return direct.effectivePermission;

  let level: EffectivePermission = 'NONE';
  for (const row of permissions) {
    if (row.parentMenuCode === menuCode) {
      level = maxPermission(level, row.effectivePermission);
    }
  }
  return level;
};

/**
 * 내 계정(일반) 「메뉴 권한」 요약.
 * NONE은 제외하고 Figma top 메뉴 순서로 표시한다.
 */
export const listReadableTopLevelPermissions = (
  permissions: MenuPermission[] | undefined | null,
): Array<{
  menuCode: TopLevelMenuCode;
  label: string;
  level: EffectivePermission;
}> => {
  if (!permissions?.length) return [];

  return TOP_LEVEL_MENU_ORDER.flatMap((menuCode) => {
    const level = resolveTopLevelPermission(permissions, menuCode);
    if (level === 'NONE') return [];
    return [
      {
        menuCode,
        label: TOP_LEVEL_MENU_LABELS[menuCode],
        level,
      },
    ];
  });
};
